import { useState, useEffect, useCallback } from 'react';

export type ThemeMode = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'theme-mode';

/**
 * Detects the user's system color scheme preference.
 */
function getSystemPreference(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Reads the persisted theme mode from localStorage.
 * Falls back to 'system' if no preference is stored.
 */
function getStoredMode(): ThemeMode {
  if (typeof window === 'undefined') return 'system';
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark' || stored === 'system') {
    return stored;
  }
  return 'system';
}

/**
 * Applies or removes the 'dark' class on the document element
 * based on the resolved theme.
 */
function applyTheme(mode: ThemeMode): void {
  const resolved = mode === 'system' ? getSystemPreference() : mode;
  const root = document.documentElement;
  if (resolved === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}

/**
 * Hook for managing dark mode with system preference detection
 * and localStorage persistence.
 *
 * Supports three modes:
 * - 'light': Always light theme
 * - 'dark': Always dark theme
 * - 'system': Follow OS preference (default)
 *
 * The resolved theme (actual light/dark) is derived from the mode.
 */
export function useDarkMode() {
  const [mode, setModeState] = useState<ThemeMode>(getStoredMode);
  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(() => {
    const m = getStoredMode();
    return m === 'system' ? getSystemPreference() : m;
  });

  const setMode = useCallback((newMode: ThemeMode) => {
    setModeState(newMode);
    localStorage.setItem(STORAGE_KEY, newMode);
    applyTheme(newMode);
    const resolved = newMode === 'system' ? getSystemPreference() : newMode;
    setResolvedTheme(resolved);
  }, []);

  const toggle = useCallback(() => {
    const next: ThemeMode = mode === 'light' ? 'dark' : mode === 'dark' ? 'system' : 'light';
    setMode(next);
  }, [mode, setMode]);

  // Apply theme on mount and listen for system preference changes
  useEffect(() => {
    applyTheme(mode);

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      if (mode === 'system') {
        applyTheme('system');
        setResolvedTheme(getSystemPreference());
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [mode]);

  return {
    /** Current mode setting: 'light', 'dark', or 'system' */
    mode,
    /** The actual resolved theme applied to the page */
    resolvedTheme,
    /** Set the theme mode explicitly */
    setMode,
    /** Toggle between light → dark → system */
    toggle,
    /** Whether dark mode is currently active */
    isDark: resolvedTheme === 'dark',
  };
}
